import { useState, useMemo } from "react";
import { AlertCircle, Activity, CheckCircle } from 'lucide-react';
import { PageContainer } from "../ui/page-container";
import SupportHeader from "./SupportHeader";
import CustomerView from "./CustomerView";
import ErrorBoundary from "./ErrorBoundary";
import { useSupportHistory } from 'core';
import type { ISupportTicket } from 'core/features/support/types';
import SupportForm from './support-form/SupportForm';


const SupportDashboard = () => {
    const [openSupportModal, setOpenSupportModal] = useState(false);

    // Fetch support tickets from API
    const { data: supportData, isFetching: supportLoading } = useSupportHistory();


    // Calculate KPI Data dynamically from API response
    const getKpiData = useMemo(() => {
        const tickets = supportData?.data || [];

            const openTickets = tickets.filter((t: ISupportTicket) => 
                t.status?.toLowerCase() === "open"
            ).length;
            
            const inProgressTickets = tickets.filter((t: ISupportTicket) => 
                t.status?.toLowerCase() === "in progress"
            ).length;
            
            const closedTickets = tickets.filter((t: ISupportTicket) => 
                t.status?.toLowerCase() === "closed"
            ).length;

            return [
                {
                    label: "My Open Tickets",
                    value: openTickets.toString(),
                    icon: AlertCircle,
                    color: "text-orange-600 dark:text-orange-300",
                    bgColor: "bg-orange-100 dark:bg-orange-900/20",
                    isPositive: false
                },
                {
                    label: "In Progress",
                    value: inProgressTickets.toString(),
                    icon: Activity,
                    color: "text-blue-600 dark:text-blue-300",
                    bgColor: "bg-blue-100 dark:bg-blue-900/20",
                    isPositive: true
                },
                {
                    label: "Closed",
                    value: closedTickets.toString(),
                    icon: CheckCircle,
                    color: "text-green-600 dark:text-green-300",
                    bgColor: "bg-green-100 dark:bg-green-900/20",
                    isPositive: true
                },
            ];
    }, [supportData]);

    return (
        <PageContainer>
            <ErrorBoundary>
                <div data-testid="support-dashboard">
                    <SupportHeader
                        kpiData={getKpiData}
                        isLoading={supportLoading}
                        onNewTicket={() => setOpenSupportModal(true)}
                    />

                    {/* One view, because the API supports one.
                        There used to be a customer/agent/manager switcher with
                        hardcoded ids (cust-001, agent-001, mgr-001). Only the
                        customer view read the real endpoint; the other two ran
                        entirely on fixtures, and GET /support returns no
                        assignee, no priority and no agent — there are no roles
                        in the data to switch between.

                        See docs/merko-api-requirement.md for what the backend
                        needs to expose before those views can return. */}
                    <div className="bg-surface shadow-sm">
                        <div data-testid="customer-view">
                            <CustomerView />
                        </div>
                    </div>

                    {/* Support Form Modal - Shared across all roles */}
                    <SupportForm 
                        open={openSupportModal} 
                        onOpenChange={() => { setOpenSupportModal(false) }} 
                    />
                </div>
            </ErrorBoundary>
        </PageContainer>
    );
};

export default SupportDashboard;
